'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { songService, scheduleService, Song, ScheduleBlock } from '@/lib/services/radioService';

// ─── Types (re-exported for consumers) ───────────────────────────────────────

export type { Song as LibrarySong, ScheduleBlock } from '@/lib/services/radioService';

// Keep legacy key exports so any remaining localStorage references don't break
export const SCHEDULE_KEY = 'airadio_schedule';
export const LIBRARY_KEY = 'airadio_library';

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function parseDurationToSeconds(dur: string): number {
  const parts = dur.split(':').map(Number);
  if (parts.length === 2) return (parts[0] ?? 0) * 60 + (parts[1] ?? 0);
  if (parts.length === 3) return (parts[0] ?? 0) * 3600 + (parts[1] ?? 0) * 60 + (parts[2] ?? 0);
  return 0;
}

export function secondsToDisplay(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getActiveBlock(blocks: ScheduleBlock[]): ScheduleBlock | null {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  const minuteFraction = now.getMinutes() / 60;
  const currentDecimalHour = hour + minuteFraction;

  return (
    blocks.find(
      (b) =>
        b.day === day &&
        currentDecimalHour >= b.startHour &&
        currentDecimalHour < b.startHour + b.durationHours
    ) ?? null
  );
}

function getNextBlock(blocks: ScheduleBlock[]): ScheduleBlock | null {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  const minuteFraction = now.getMinutes() / 60;
  const currentDecimalHour = hour + minuteFraction;

  const sameDay = blocks
    .filter((b) => b.day === day && b.startHour > currentDecimalHour)
    .sort((a, b) => a.startHour - b.startHour);
  if (sameDay.length > 0) return sameDay[0] ?? null;

  for (let d = 1; d <= 7; d++) {
    const nextDay = (day + d) % 7;
    const nextDayBlocks = blocks
      .filter((b) => b.day === nextDay)
      .sort((a, b) => a.startHour - b.startHour);
    if (nextDayBlocks.length > 0) return nextDayBlocks[0] ?? null;
  }
  return null;
}

function msUntilBlockStart(block: ScheduleBlock): number {
  const now = new Date();
  const target = new Date(now);
  const dayDiff = (block.day - now.getDay() + 7) % 7;
  target.setDate(now.getDate() + dayDiff);
  target.setHours(block.startHour, 0, 0, 0);
  if (target <= now) target.setDate(target.getDate() + 7);
  return target.getTime() - now.getTime();
}

// ─── Shuffle ──────────────────────────────────────────────────────────────────

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

// ─── Context ─────────────────────────────────────────────────────────────────

export interface PlaybackState {
  schedule: ScheduleBlock[];
  library: Song[];
  activeBlock: ScheduleBlock | null;
  nextBlock: ScheduleBlock | null;
  currentSong: Song | null;
  currentSongIndex: number;
  queue: Song[];
  isPlaying: boolean;
  elapsed: number;
  totalDuration: number;
  play: () => void;
  pause: () => void;
  skipNext: () => void;
  skipPrev: () => void;
  setVolume: (v: number) => void;
  volume: number;
  muted: boolean;
  toggleMute: () => void;
}

const PlaybackContext = createContext<PlaybackState | null>(null);

export function usePlayback(): PlaybackState {
  const ctx = useContext(PlaybackContext);
  if (!ctx) throw new Error('usePlayback must be used inside PlaybackProvider');
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function PlaybackProvider({ children }: { children: React.ReactNode }) {
  const [schedule, setSchedule] = useState<ScheduleBlock[]>([]);
  const [library, setLibrary] = useState<Song[]>([]);
  const [activeBlock, setActiveBlock] = useState<ScheduleBlock | null>(null);
  const [nextBlock, setNextBlock] = useState<ScheduleBlock | null>(null);
  const [queue, setQueue] = useState<Song[]>([]);
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [volume, setVolumeState] = useState(80);
  const [muted, setMuted] = useState(false);

  const elapsedRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transitionRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Load from Supabase ────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      const [blocksData, songsData] = await Promise.all([
        scheduleService.getAll(),
        songService.getAll(),
      ]);
      setSchedule(blocksData);
      setLibrary(songsData);
    } catch {
      // non-critical for public page — silently fail
    }
  }, []);

  useEffect(() => {
    loadData();
    // Refresh every 60 seconds to pick up admin changes
    const poll = setInterval(loadData, 60_000);
    return () => clearInterval(poll);
  }, [loadData]);

  // ── Determine active block ────────────────────────────────────────────────
  const refreshActiveBlock = useCallback(() => {
    const active = getActiveBlock(schedule);
    const next = getNextBlock(schedule);
    setActiveBlock(active);
    setNextBlock(next);
    return { active, next };
  }, [schedule]);

  useEffect(() => {
    const { active, next } = refreshActiveBlock();

    if (active && library.length > 0) {
      const songs = active.songIds
        .map((id) => library.find((s) => s.id === id))
        .filter((s): s is Song => !!s && !!s.fileUrl);

      if (songs.length > 0) {
        setQueue(shuffleArray(songs));
        setCurrentSongIndex(0);
        elapsedRef.current = 0;
        setElapsed(0);
      } else {
        // Active block exists but has no playable songs — fall back to full library
        const fallback = library.filter((s) => s.fileUrl);
        setQueue(shuffleArray(fallback));
        setCurrentSongIndex(0);
        elapsedRef.current = 0;
        setElapsed(0);
      }
    } else if (library.length > 0) {
      // No active block — play all uploaded tracks as a continuous stream
      const fallback = library.filter((s) => s.fileUrl);
      setQueue(shuffleArray(fallback));
      setCurrentSongIndex(0);
      elapsedRef.current = 0;
      setElapsed(0);
    } else {
      setQueue([]);
      setCurrentSongIndex(0);
    }

    if (transitionRef.current) clearTimeout(transitionRef.current);
    if (next) {
      const ms = msUntilBlockStart(next);
      if (ms > 0 && ms < 24 * 60 * 60 * 1000) {
        transitionRef.current = setTimeout(() => {
          refreshActiveBlock();
        }, ms);
      }
    }

    return () => {
      if (transitionRef.current) clearTimeout(transitionRef.current);
    };
  }, [schedule, library, refreshActiveBlock]);

  // ── Tick ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);

    if (isPlaying && queue.length > 0) {
      tickRef.current = setInterval(() => {
        const currentSong = queue[currentSongIndex];
        const total = currentSong ? parseDurationToSeconds(currentSong.duration) : 0;

        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);

        if (total > 0 && elapsedRef.current >= total) {
          const nextIdx = currentSongIndex + 1;
          if (nextIdx < queue.length) {
            setCurrentSongIndex(nextIdx);
            elapsedRef.current = 0;
            setElapsed(0);
          } else {
            setCurrentSongIndex(0);
            elapsedRef.current = 0;
            setElapsed(0);
          }
        }
      }, 1_000);
    }

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [isPlaying, queue, currentSongIndex]);

  // ── Re-check active block every minute ───────────────────────────────────
  useEffect(() => {
    const minuteCheck = setInterval(() => {
      refreshActiveBlock();
    }, 60_000);
    return () => clearInterval(minuteCheck);
  }, [refreshActiveBlock]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const currentSong = queue[currentSongIndex] ?? null;
  const totalDuration = currentSong ? parseDurationToSeconds(currentSong.duration) : 0;

  // ── Actions ───────────────────────────────────────────────────────────────
  const play = useCallback(() => setIsPlaying(true), []);
  const pause = useCallback(() => setIsPlaying(false), []);

  const skipNext = useCallback(() => {
    if (queue.length === 0) return;
    const nextIdx = (currentSongIndex + 1) % queue.length;
    setCurrentSongIndex(nextIdx);
    elapsedRef.current = 0;
    setElapsed(0);
  }, [queue, currentSongIndex]);

  const skipPrev = useCallback(() => {
    if (queue.length === 0) return;
    const prevIdx = (currentSongIndex - 1 + queue.length) % queue.length;
    setCurrentSongIndex(prevIdx);
    elapsedRef.current = 0;
    setElapsed(0);
  }, [queue, currentSongIndex]);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    setMuted(false);
  }, []);

  const toggleMute = useCallback(() => setMuted((m) => !m), []);

  return (
    <PlaybackContext.Provider
      value={{
        schedule,
        library,
        activeBlock,
        nextBlock,
        currentSong,
        currentSongIndex,
        queue,
        isPlaying,
        elapsed,
        totalDuration,
        play,
        pause,
        skipNext,
        skipPrev,
        setVolume,
        volume,
        muted,
        toggleMute,
      }}
    >
      {children}
    </PlaybackContext.Provider>
  );
}
